/**
 * ReferralStatistics component - displays referral statistics cards
 */
import React from "react";
import { Card, Group, Stack, Text, Tooltip } from "@mantine/core";
import {
  IconClock,
  IconInfoCircle,
  IconUsers,
  IconUserCheck,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useReferralStore } from "@/stores/referralStore";

export const ReferralStatistics: React.FC = () => {
  const dashboard = useReferralStore((state) => state.dashboard);

  if (!dashboard?.statistics) {
    return null;
  }

  const { statistics } = dashboard;

  return (
    <Group grow>
      {/* Leads */}
      <Card p="lg" radius="md" style={{ backgroundColor: "#eff6ff" }}>
        <Stack gap="xs">
          <Group justify="space-between" align="start">
            <Group gap={4}>
              <Text size="sm" c="dimmed" fw={500}>
                Leads
              </Text>
              <Tooltip
                label="Number of people who signed up as leads (free tier)"
                multiline
                w={220}
              >
                <IconInfoCircle
                  size={14}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
              </Tooltip>
            </Group>
            <IconUsers size={20} color="#3b82f6" />
          </Group>
          <Text size="2rem" fw={700} c="blue.7">
            {statistics.leads ?? 0}
          </Text>
        </Stack>
      </Card>

      {/* Converted Customers */}
      <Card p="lg" radius="md" style={{ backgroundColor: "#f0fdf4" }}>
        <Stack gap="xs">
          <Group justify="space-between" align="start">
            <Group gap={4}>
              <Text size="sm" c="dimmed" fw={500}>
                Converted Customers
              </Text>
              <Tooltip
                label="Number of people who signed up and converted to paying customers"
                multiline
                w={220}
              >
                <IconInfoCircle
                  size={14}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
              </Tooltip>
            </Group>
            <IconUserCheck size={20} color="#10b981" />
          </Group>
          <Text size="2rem" fw={700} c="green.7">
            {statistics.convertedCustomers ?? 0}
          </Text>
        </Stack>
      </Card>

      {/* Pending Payment */}
      <Card p="lg" radius="md" style={{ backgroundColor: "#fef3c7" }}>
        <Stack gap="xs">
          <Group justify="space-between" align="start">
            <Group gap={4}>
              <Text size="sm" c="dimmed" fw={500}>
                Pending Payment
              </Text>
              <Tooltip
                label="Amount currently being processed and awaiting payment"
                multiline
                w={220}
              >
                <IconInfoCircle
                  size={14}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
              </Tooltip>
            </Group>
            <IconClock size={20} color="#f59e0b" />
          </Group>
          <Text size="2rem" fw={700} c="yellow.8">
            ${statistics.totalPending.toFixed(2)}
          </Text>
        </Stack>
      </Card>

      {/* Total Referrals */}
      <Card p="lg" radius="md" style={{ backgroundColor: "#f3e8ff" }}>
        <Stack gap="xs">
          <Group justify="space-between" align="start">
            <Group gap={4}>
              <Text size="sm" c="dimmed" fw={500}>
                Total Referrals
              </Text>
              <Tooltip
                label="Total number of people who signed up using your referral link"
                multiline
                w={220}
              >
                <IconInfoCircle
                  size={14}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
              </Tooltip>
            </Group>
            <IconUsersGroup size={20} color="#9333ea" />
          </Group>
          <Text size="2rem" fw={700} c="violet.7">
            {statistics.total}
          </Text>
        </Stack>
      </Card>
    </Group>
  );
};
